import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useResource, formatDateTime } from "../lib/hooks";
import type { Notification } from "../lib/types";

const POLL_MS = 30_000;

/**
 * Every signed-in account's own feed: a claim approved or declined, a new
 * claim waiting on a manager. In-app only for now — the notify() service
 * behind this is built so email/push can be added without this component
 * changing at all.
 */
export const NotificationBell = () => {
    const feed = useResource<{ items: Notification[]; unreadCount: number }>("/notifications");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const id = setInterval(() => feed.reload(), POLL_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!open) return;
        const close = (event: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const open_ = (n: Notification) => {
        setOpen(false);
        if (!n.read_at) void api.post(`/notifications/${n.id}/read`, {}).then(() => feed.reload());
        if (n.link) navigate(n.link);
    };

    const markAllRead = async () => {
        await api.post("/notifications/read-all", {});
        feed.reload();
    };

    const unread = feed.data?.unreadCount ?? 0;

    return (
        <div className="notif-wrap" ref={wrapRef}>
            <button className="notif-bell" aria-haspopup="menu" aria-expanded={open}
                onClick={() => setOpen((v) => !v)} aria-label="Notifications">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
            </button>
            {open && (
                <div className="notif-menu" role="menu">
                    <div className="notif-menu-head">
                        <span>Notifications</span>
                        {unread > 0 && (
                            <button className="icon-btn small" onClick={() => void markAllRead()}>Mark all read</button>
                        )}
                    </div>
                    {(feed.data?.items ?? []).length === 0 ? (
                        <div className="empty" style={{ padding: "1.2rem" }}>Nothing yet.</div>
                    ) : (
                        (feed.data?.items ?? []).map((n) => (
                            <button key={n.id} className={`notif-item ${n.read_at ? "" : "unread"}`}
                                onClick={() => open_(n)}>
                                <div className="notif-title">{n.title}</div>
                                <div className="notif-body">{n.body}</div>
                                <div className="notif-time">{formatDateTime(n.created_at)}</div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
